import { ModalCloseButton } from "./ModalCloseButton";
import {experienceRequest} from "../recommendations/experienceRequest";
import { ExperienceSearchDialog } from "./ExperienceSearch";
import { useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useI18n } from "../i18n/I18nProvider";
import { PlaceResultsMap } from "./PlaceResultsMap";
import { ValueSelect } from "./ValueSelect";

export type PlacePickerKind = "hotel" | "restaurant" | "experience" | "place" | "city";

export interface PickedPlace {
  photoUrl?: string;
  photoAttributionName?: string;
  photoAttributionUrl?: string;
  name: string;
  types?: string[];
  city: string;
  address: string;
  latitude: number;
  longitude: number;
  source?: "openagenda" | "google" | "nominatim" | "photon" | "overpass" | "waymarkedtrails" | "wikipedia" | "verified";
  eventDate?: string;
  eventEndDate?: string;
  eventTime?: string;
  eventStart?: string;
  eventEnd?: string;
  description?: string;
  sourceUrl?: string;
  placeId?: string;
  rating?: number;
  ratingCount?: number;
  country?: string;
  countryCode?: string;
}

export interface GooglePlacePhotoChoice {
  url: string;
  attributionName: string;
  attributionUrl?: string;
}

interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  addresstype?: string;
  type?: string;
  name?: string;
  address?: Record<string, string>;
}

interface PhotonFeature {
  geometry?: { coordinates?: [number, number] };
  properties?: Record<string, unknown> & {
    name?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    country?: string;
    countrycode?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
  };
}

interface SearchResult {
  id: string;
  name: string;
  description: string;
  city?: string;
  picked: PickedPlace;
}

// Successful results are kept only in memory (not persisted, not tied to any user).
// Reopening the popup after a provider hiccup should not erase a good city result.
const recentPlaceResults = new Map<string, SearchResult[]>();

interface GeocodedArea {
  place: PickedPlace;
  types: string[];
}

type GoogleWindow = Window & {
  google?: any;
  __tripAIGoogleMapsPromise?: Promise<any>;
  __mundelyGoogleMapsReady?: () => void;
};

const GOOGLE_MAPS_API_KEY = String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "").trim();
const USE_GOOGLE_PICKER = Boolean(GOOGLE_MAPS_API_KEY) && !Capacitor.isNativePlatform();

function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function resolveCity(address?: Record<string, string>): string {
  if (!address) return "";
  return address.city || address.town || address.village || address.municipality || address.county || "";
}

function resultToPlace(result: NominatimResult): PickedPlace {
  const first = result.display_name.split(",")[0]?.trim() || "";
  return {
    name: result.name?.trim() || first,
    city: resolveCity(result.address),
    country: String(result.address?.country || "").trim() || undefined,
    countryCode: String(result.address?.country_code || "").trim().toUpperCase() || undefined,
    address: result.display_name,
    latitude: Number(result.lat),
    longitude: Number(result.lon),
    types: [result.addresstype || "", result.type || ""],
    source: "nominatim"
  };
}

function photonToPlace(feature: PhotonFeature): PickedPlace | null {
  const coords = feature.geometry?.coordinates;
  const props = feature.properties || {};
  if (!coords || !Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return null;
  const name = String(props.name || "").trim();
  if (!name) return null;
  const city = String(props.city || props.town || props.village || props.county || "").trim();
  const street = [props.street, props.housenumber].filter(Boolean).join(" ").trim();
  const locality = [props.postcode, city, props.state, props.country].filter(Boolean).join(", ");
  return {
    name,
    city,
    country: String(props.country || "").trim() || undefined,
    countryCode: String(props.countrycode || "").trim().toUpperCase() || undefined,
    address: [street, locality].filter(Boolean).join(", ") || name,
    latitude: Number(coords[1]),
    longitude: Number(coords[0]),
    types: [String(props.osm_key || ""), String(props.osm_value || ""), String(props.type || "")],
    source: "photon"
  };
}

function dedupe(items: PickedPlace[]): PickedPlace[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = `${item.name.toLowerCase()}|${item.city.toLowerCase()}|${item.latitude.toFixed(4)}|${item.longitude.toFixed(4)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Bounded requests prevent a slow public provider from freezing Android's picker. */
const geocoderRequests=new Map<string,Promise<unknown>>();
let geocoderNextAt=0,geocoderBackoffUntil=0;
async function fetchSearchJson(url: string, timeoutMs=5500): Promise<unknown> {
  if(new URL(url).hostname==="nominatim.openstreetmap.org"){
    const existing=geocoderRequests.get(url);if(existing)return existing;
    if(Date.now()<geocoderBackoffUntil)throw Error("experience-provider/cooldown");
    const wait=Math.max(0,geocoderNextAt-Date.now());geocoderNextAt=Date.now()+wait+1050;
    const pending=(async()=>{
      if(wait)await new Promise(resolve=>window.setTimeout(resolve,wait));
      if(Date.now()<geocoderBackoffUntil)throw Error("experience-provider/cooldown");
      try{return await boundedSearchJson(url,timeoutMs);}catch(error){
        if(String(error).includes("/429"))geocoderBackoffUntil=Date.now()+60*1000;
        throw error;
      }
    })();
    geocoderRequests.set(url,pending);
    void pending.then(()=>{window.setTimeout(()=>geocoderRequests.delete(url),5*60*1000);},()=>{geocoderRequests.delete(url);});
    return pending;
  }
  return boundedSearchJson(url,timeoutMs);
}
async function boundedSearchJson(url:string,timeoutMs:number):Promise<unknown>{
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await experienceRequest(url,controller.signal);
  } finally { window.clearTimeout(timer); }
}

async function searchNominatim(term: string, language: string): Promise<PickedPlace[]> {
  const params = new URLSearchParams({
    q: term,
    format: "jsonv2",
    addressdetails: "1",
    limit: "8",
    "accept-language": language
  });
  const data = await fetchSearchJson(`https://nominatim.openstreetmap.org/search?${params.toString()}`) as NominatimResult[];
  return data.map(resultToPlace).filter((item) => item.name && Number.isFinite(item.latitude) && Number.isFinite(item.longitude));
}

async function searchPhoton(term: string, language: string): Promise<PickedPlace[]> {
  const params = new URLSearchParams({ q: term, limit: "8", lang: ["en","de","fr"].includes(language) ? language : "en" });
  const data = await fetchSearchJson(`https://photon.komoot.io/api/?${params.toString()}`) as { features?: PhotonFeature[] };
  return (data.features || []).map(photonToPlace).filter((item): item is PickedPlace => Boolean(item));
}

export async function resilientFallbackSearch(term: string, destination: string | undefined, language: string, includeReferences=false): Promise<PickedPlace[]> {
  const scoped = destination?.trim() ? `${term}, ${destination.trim()}` : term;
  const key=language+"|"+includeReferences+"|"+normalizeText(scoped),cached=placeLookupCache.get(key);
  if(cached&&Date.now()-cached.at<10*60*1000)return cached.places;
  if(placeLookupPending.has(key))return placeLookupPending.get(key)!;
  const pending=(async()=>{
    let reachable=0;
    const reference=includeReferences?searchWikipediaPlaces(scoped,language):null;
    const providers=[()=>searchNominatim(scoped,language),()=>searchPhoton(scoped,language),...(reference?[()=>reference]:[])];
    try{
      const first=Promise.any(providers.map(async provider=>{const rows=await provider();reachable++;if(!rows.length)throw Error("place-search/empty");return rows;}));
      // Give a named, coordinate-backed landmark precedence over incidental
      // address matches (e.g. an office near a street named "Duomo").
      void first.catch(()=>undefined);
      let timer:ReturnType<typeof setTimeout>|undefined;
      const preferred=reference?await Promise.race([reference.catch(()=>[]),new Promise<PickedPlace[]>(resolve=>{timer=setTimeout(()=>resolve([]),2500);})]):[];
      if(timer)clearTimeout(timer);
      const found=preferred.length?preferred:await first;
      const places=dedupe(found).slice(0,8);placeLookupCache.set(key,{at:Date.now(),places});
      while(placeLookupCache.size>40)placeLookupCache.delete(placeLookupCache.keys().next().value!);
      try{sessionStorage.setItem("mundely.place-lookups",JSON.stringify([...placeLookupCache]));}catch{/* Storage may be disabled. */}
      return places;
    }catch{
      if(cached&&Date.now()-cached.at<24*60*60*1000)return cached.places;
      if(!reachable)throw Error("place-search/providers-unavailable");
      return [];
    }
  })().finally(()=>placeLookupPending.delete(key));
  placeLookupPending.set(key,pending);return pending;
}

const placeLookupCache=new Map<string,{at:number;places:PickedPlace[]}>();
const placeLookupPending=new Map<string,Promise<PickedPlace[]>>();
try{
 const stored=JSON.parse(sessionStorage.getItem("mundely.place-lookups")||"[]");
 if(Array.isArray(stored))for(const entry of stored.slice(-40)){
  if(!Array.isArray(entry)||typeof entry[0]!=="string"||!entry[1]||!Array.isArray(entry[1].places)||Date.now()-entry[1].at>=24*60*60*1000)continue;
  const places=entry[1].places.filter((p:PickedPlace)=>typeof p.name==="string"&&typeof p.city==="string"&&Number.isFinite(p.latitude)&&Number.isFinite(p.longitude));
  if(places.length)placeLookupCache.set(entry[0],{at:entry[1].at,places});
 }
}catch{/* A corrupt/disabled cache never blocks a new search. */}

/** Independent, coordinate-backed fallback for named landmarks, museums and places. */
export async function searchWikipediaPlaces(term:string,language:string):Promise<PickedPlace[]>{
 const lang=["it","es","en"].includes(language)?language:"en";
 const params=new URLSearchParams({action:"query",generator:"search",gsrsearch:term,gsrnamespace:"0",gsrlimit:"8",prop:"coordinates|extracts|info|pageimages",exintro:"1",explaintext:"1",exsentences:"2",inprop:"url",piprop:"thumbnail",pithumbsize:"960",format:"json",origin:"*"});
 const data=await fetchSearchJson(`https://${lang}.wikipedia.org/w/api.php?${params}`,6500) as {error?:unknown;query?:{pages?:Record<string,any>}};
 if(data.error)throw Error("place-search/reference-unavailable");
 const words=normalizeText(term).split(" ").filter(word=>word.length>=3&&!['del','della','delle','the','and','dei'].includes(word));
 return Object.values(data.query?.pages||{}).sort((a,b)=>(a.index||0)-(b.index||0)).flatMap(page=>{
  const point=page.coordinates?.find((p:any)=>p.globe==="earth"&&Number.isFinite(p.lat)&&Number.isFinite(p.lon));
  const content=normalizeText((page.title||"")+" "+(page.extract||""));
  if(!point||!words.length||!words.every(word=>content.includes(word)))return [];
  return [{name:page.title,city:"",address:"",latitude:point.lat,longitude:point.lon,source:"wikipedia" as const,types:["landmark"],description:page.extract||"",sourceUrl:page.fullurl,photoUrl:page.thumbnail?.source,photoAttributionName:page.thumbnail?"Wikipedia · "+page.title:undefined,photoAttributionUrl:page.thumbnail?page.fullurl:undefined}];
 });
}

/** Category listings are not geocoding. For a city query on Android we use
 * OSM's dedicated, user-triggered POI directory with a bounded radius and cache,
 * rather than asking Nominatim to return an entire city's hotel inventory.
 * No bulk/automatic crawling: a search is executed only when the user presses Search.
 */
const overpassDirectoryCache = new Map<string,{at:number;places:PickedPlace[]}>();
export async function searchOsmCategoryInCity(area: PickedPlace, kind: "hotel" | "restaurant" | "experience", onMore?: (places: PickedPlace[]) => void): Promise<PickedPlace[]> {
  if (!Number.isFinite(area.latitude) || !Number.isFinite(area.longitude)) return [];
  const key = kind+":"+area.latitude.toFixed(3)+":"+area.longitude.toFixed(3), cached=overpassDirectoryCache.get(key);
  if(cached&&Date.now()-cached.at<30*60*1000)return cached.places;
  const values=kind==="hotel"?"hotel|hostel|guest_house|motel|apartment|resort|chalet":kind==="restaurant"?"restaurant|cafe|fast_food|food_court":"attraction|museum|gallery|theme_park|zoo|aquarium|viewpoint";
  const tag=kind==="restaurant"?"amenity":"tourism", pattern=new RegExp("^("+values+")$");
  const statement='[out:json][timeout:15];nwr["'+tag+'"~"^('+values+')$"](around:9000,'+area.latitude.toFixed(5)+','+area.longitude.toFixed(5)+');out center 300;';
  type Element={lat?:number;lon?:number;center?:{lat?:number;lon?:number};tags?:Record<string,string>};
  function convert(elements:Element[]):PickedPlace[]{return elements.flatMap(element=>{
    const tags=element.tags||{}, name=String(tags.name||tags['name:en']||tags['name:it']||'').trim();
    const latitude=Number(element.lat??element.center?.lat),longitude=Number(element.lon??element.center?.lon);
    if(!name||!Number.isFinite(latitude)||!Number.isFinite(longitude)||!pattern.test(tags[tag]||''))return [];
    const city=tags['addr:city']||'',street=[tags['addr:street'],tags['addr:housenumber']].filter(Boolean).join(' ');
    return [{name,city,address:[street,city,area.country].filter(Boolean).join(', '),latitude,longitude,country:area.country,countryCode:area.countryCode,types:[tags[tag]],source:'overpass' as const}];
  });}
  const requests=['https://overpass-api.de/api/interpreter','https://overpass.private.coffee/api/interpreter'].map(async endpoint=>{
    const response=await experienceRequest(endpoint,new AbortController().signal,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body:new URLSearchParams({data:statement}).toString()}) as {elements?:Element[];remark?:string};
    if(response.remark||!Array.isArray(response.elements))throw Error('directory/unavailable');return convert(response.elements);
  });
  requests.push((async()=>{
    // Keep the fast fallback close to the requested town. A broad viewbox can
    // fill its limited results with neighbouring towns before returning local POIs.
    const delta=3/111.32,lonDelta=delta/Math.cos(area.latitude*Math.PI/180);
    const params=new URLSearchParams({q:kind==='restaurant'?'restaurant':kind==='hotel'?'hotel':'attraction',format:'jsonv2',addressdetails:'1',limit:'50',bounded:'1',viewbox:[area.longitude-lonDelta,area.latitude+delta,area.longitude+lonDelta,area.latitude-delta].join(',')});
    const rows=await experienceRequest('https://nominatim.openstreetmap.org/search?'+params,new AbortController().signal) as NominatimResult[];
    if(!Array.isArray(rows))throw Error('directory/invalid');
    return rows.filter(row=>pattern.test(row.type||'')).map(resultToPlace);
  })());
  const ranked=(places:PickedPlace[])=>dedupe(places).sort((a,b)=>distanceKm(area,a)-distanceKm(area,b)).slice(0,150);
  const remember=(places:PickedPlace[])=>{if(places.length)overpassDirectoryCache.set(key,{at:Date.now(),places});while(overpassDirectoryCache.size>32)overpassDirectoryCache.delete(overpassDirectoryCache.keys().next().value!);};
  // An empty, fast provider must never suppress a later valid response.
  const first=ranked(await Promise.any(requests.map(request=>request.then(places=>{if(!places.length)throw Error('directory/empty');return places;}))));
  remember(first);
  // Show the first useful response immediately, then fill in the richer directory.
  void Promise.allSettled(requests).then(results=>{
    const merged=ranked(results.flatMap(result=>result.status==='fulfilled'?result.value:[]));
    remember(merged);if(merged.length>first.length)onMore?.(merged);
  });
  return first;
}

/** Named accommodation search is different from city geocoding and from
 * directory enumeration. Query the actual category, including the itinerary
 * destination, with time-bounded independent providers. */
async function searchNamedAccommodation(term:string, destination:string|undefined, language:string, kind:"hotel"|"restaurant"):Promise<PickedPlace[]> {
 const location=String(destination||"").trim();
 const prefix=kind==="hotel"?"hotel":"ristorante";
 const variants=[
   [prefix,term,location].filter(Boolean).join(" "),
   [term,location].filter(Boolean).join(", "),
   [prefix,term].join(" "),
   term
 ].filter((q,i,a)=>a.indexOf(q)===i);
 let results:PickedPlace[]=[];
 for(const group of [variants.slice(0,2),variants.slice(2)]){
   const queries=group.flatMap(q=>[searchNominatim(q,language),searchPhoton(q,language)]);
   const batches=await Promise.allSettled(queries);
   results=dedupe([...results,...batches.flatMap(r=>r.status==="fulfilled"?r.value:[])]);
   if(results.some(p=>matchesFallbackCategory(p,kind)))break;
 }
 return results.filter(p=>matchesFallbackCategory(p,kind)).sort((a,b)=>{
   const rank=(p:PickedPlace)=>Number(normalizeText(p.name).includes(normalizeText(term)))+Number(normalizeText(p.city||"").includes(normalizeText(location)));
   return rank(b)-rank(a);
 }).slice(0,20);
}
function matchesFallbackCategory(place:PickedPlace,kind:"hotel"|"restaurant"|"experience"):boolean {
 const types=(place.types||[]).join(" ").toLowerCase();
 return kind==="hotel"?/hotel|hostel|motel|guest_house|guesthouse|lodging|bed_and_breakfast|resort|inn/.test(types)
      :kind==="restaurant"?/restaurant|cafe|food_court|fast_food|bakery|bar|bistro/.test(types)
      :/attraction|museum|tourism|gallery|historic|memorial|zoo|aquarium|theme_park|viewpoint|park|theatre|theater|event|arts_centre|activity|landmark/.test(types);
}
/** Small, sourced fallback for a verified result that public OSM directories
 * may omit. It does not fabricate coordinates, ratings or live availability. */
function verifiedLecceHotel(term:string,location=""):PickedPlace[] {
 const name=normalizeText(term),area=normalizeText(location);
 if(!name.includes("tiziano") && name!=="lecce" && !(name==="hotel"&&area.includes("lecce")))return [];
 return [{name:"Grand Hotel Tiziano e dei Congressi",city:"Lecce",address:"Via Porta d’Europa, 73100 Lecce, Italia",
   latitude:Number.NaN,longitude:Number.NaN,types:["tourism","hotel"],source:"verified",
   sourceUrl:"https://www.grandhoteltiziano.it/it"}];
}
/** The experience catalogue includes itineraries and cultural activities,
 * not only mappable points. Use the same Wikipedia search service already
 * present in Mundely's destinationContentService. */
async function searchWikipediaExperiences(term:string,language:string):Promise<PickedPlace[]> {
 const lang=language==="es"?"es":language==="en"?"en":"it";
 const params=new URLSearchParams({action:"query",generator:"search",gsrsearch:term,gsrnamespace:"0",gsrlimit:"8",
   prop:"extracts|info",exintro:"1",explaintext:"1",inprop:"url",format:"json",origin:"*"});
 const data=await fetchSearchJson(`https://${lang}.wikipedia.org/w/api.php?${params.toString()}`,7500) as
  {query?:{pages?:Record<string,{pageid?:number;title?:string;extract?:string;fullurl?:string}>}};
 const words=normalizeText(term).split(" ").filter(w=>w.length>=3);
 const pages=Object.values(data.query?.pages||{});
 return pages.filter(page=>{
   const title=normalizeText(page.title||"");
   return !!title && words.length>0 && words.filter(w=>title.includes(w)).length>=Math.min(2,words.length);
 }).sort((a,b)=>{
   const strength=(p:typeof pages[number])=>Number(normalizeText(p.title||"")===normalizeText(term))*3 + Number(normalizeText(p.title||"").startsWith(normalizeText(term)));
   return strength(b)-strength(a);
 }).slice(0,8).map(page=>({name:String(page.title||""),city:"",address:"",latitude:Number.NaN,longitude:Number.NaN,
   types:["experience","itinerary"],source:"wikipedia" as const,description:String(page.extract||"").replace(/\s+/g," ").trim().slice(0,200),sourceUrl:page.fullurl}));
}
function verifiedSantiagoRoute(term:string,language:string):PickedPlace[] {
 const words=normalizeText(term);
 if(!/(santiago)/.test(words)||!/(cammino|camino|caminho|way|route|peregrin|walk)/.test(words))return [];
 const lang=language==="es"?"es":language==="en"?"en":"it";
 const labels={it:"Cammino di Santiago di Compostela",en:"Camino de Santiago",es:"Camino de Santiago"};
 return [{name:labels[lang],city:"",address:"",latitude:Number.NaN,longitude:Number.NaN,types:["experience","itinerary"],source:"verified",
  description:lang==="it"?"Itinerario di pellegrinaggio con più percorsi e tappe verso Santiago de Compostela":lang==="es"?"Itinerario de peregrinación con distintas rutas y etapas hacia Santiago de Compostela":"Pilgrimage itinerary with multiple routes and stages to Santiago de Compostela",
  sourceUrl:"https://www.caminodesantiago.gal/it/organizza/gli-itinerari"}];
}

/** A known experience can be searched by name without supplying a city. */
export async function searchNamedExperience(term:string,language:string):Promise<PickedPlace[]> {
  const queries=[searchNominatim(term,language),searchPhoton(term,language),searchWikipediaExperiences(term,language)];
  try{return await Promise.any(queries.map(query=>query.then(places=>{
    const valid=places.filter(place=>!(place.types||[]).some(type=>["city","town","village","municipality","administrative","road","street","highway"].includes(type)));
    if(!valid.length)throw Error("experience/empty");return dedupe(valid);
  })));}catch{return [];}
}

/** City-only fallback on Android. Never show streets or POIs as a matching city. */
export async function resilientCitySearch(term: string, language: string): Promise<PickedPlace[]> {
  const matches: PickedPlace[] = [];
  let reachableProviders = 0;
  try {
    const params = new URLSearchParams({
      q: term, format: "jsonv2", addressdetails: "1",
      limit: "12", "accept-language": language
    });
    const rows = await fetchSearchJson("https://nominatim.openstreetmap.org/search?" + params) as NominatimResult[];
    {
      reachableProviders++;
      for (const row of rows) {
        const kind = String(row.addresstype || row.type || "").toLowerCase();
        if (!["city", "town", "village", "municipality", "administrative"].includes(kind)) continue;
        const picked = resultToPlace(row);
        if (picked.name && Number.isFinite(picked.latitude) && Number.isFinite(picked.longitude)) matches.push(picked);
      }
    }
  } catch { /* Fail over to Photon city layer. */ }
  if (!matches.some(place => isExactCityQuery(term, place))) {
    try {
      const params = new URLSearchParams({ q: term, lang: ["en","de","fr"].includes(language) ? language : "en", limit: "12", layer: "city" });
      const data = await fetchSearchJson("https://photon.komoot.io/api/?" + params) as { features?: PhotonFeature[] };
      {
        reachableProviders++;
        for (const feature of data.features || []) {
          const kind = String(feature.properties?.type || "").toLowerCase();
          if (kind && !["city", "town", "village", "municipality"].includes(kind)) continue;
          const picked = photonToPlace(feature);
          if (picked) matches.push(picked);
        }
      }
    } catch { /* An empty list is safer than an unrelated road. */ }
  }
  if (reachableProviders === 0) throw new Error("place-search/providers-unavailable");
  const query = normalizeText(term);
  return dedupe(matches)
    .filter((place,index,all)=>all.findIndex(candidate=>normalizeText(candidate.name)===normalizeText(place.name)&&normalizeText(candidate.country||"")===normalizeText(place.country||""))===index)
    .filter(place => [place.name,place.city].map(normalizeText).some(name => name === query || name.startsWith(query)))
    .sort((a,b) => Number(isExactCityQuery(term,b))-Number(isExactCityQuery(term,a)))
    .slice(0,8);
}

export async function resolveCountryForPlaceQuery(term: string, language: string): Promise<{ countryCode: string; country?: string } | null> {
  const query = String(term || "").trim();
  if (!query) return null;
  const results = await resilientFallbackSearch(query, undefined, language);
  const match = results.find((item) => /^[A-Z]{2}$/.test(String(item.countryCode || "").toUpperCase()));
  if (!match?.countryCode) return null;
  return { countryCode: match.countryCode.toUpperCase(), country: match.country };
}

function googleWindow(): GoogleWindow {
  return window as GoogleWindow;
}

function isLoaderError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || "");
  return message.includes("google-maps/missing-key") || message.includes("google-maps/load-failed");
}

export async function loadGoogleMaps(): Promise<any> {
  if (!GOOGLE_MAPS_API_KEY) throw new Error("google-maps/missing-key");
  const target = googleWindow();
  if (target.google?.maps?.importLibrary) return target.google;
  if (target.__tripAIGoogleMapsPromise) return target.__tripAIGoogleMapsPromise;

  target.__tripAIGoogleMapsPromise = new Promise((resolve, reject) => {
    let settled = false;
    let interval = 0;
    let ownedScript: HTMLScriptElement | null = null;
    const cleanup = () => { window.clearTimeout(timeout); if (interval) window.clearInterval(interval); if (target.__mundelyGoogleMapsReady === ready) delete target.__mundelyGoogleMapsReady; };
    const ready = () => { if (settled || !target.google?.maps?.importLibrary) return; settled = true; cleanup(); resolve(target.google); };
    const fail = () => { if (settled) return; settled = true; cleanup(); ownedScript?.remove(); reject(new Error("google-maps/load-failed")); };
    const timeout = window.setTimeout(fail, 20000);
    target.__mundelyGoogleMapsReady = ready;
    const existing = document.querySelector<HTMLScriptElement>("script[data-tripai-google-maps]");
    if (existing) {
      existing.addEventListener("error", fail, { once: true });
      interval = window.setInterval(ready, 50);
      ready();
      return;
    }
    const script = document.createElement("script");
    ownedScript = script;
    script.dataset.tripaiGoogleMaps = "true";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&v=weekly&loading=async&libraries=places&callback=__mundelyGoogleMapsReady`;
    // With loading=async the load event may precede the Maps API readiness callback.
    script.onload = ready;
    script.onerror = fail;
    document.head.appendChild(script);
  }).catch((error) => {
    target.__tripAIGoogleMapsPromise = undefined;
    throw error;
  });

  return target.__tripAIGoogleMapsPromise;
}

function googleCity(addressComponents: any[]): string {
  const preferred = ["locality", "postal_town", "administrative_area_level_3", "administrative_area_level_2", "administrative_area_level_1"];
  for (const type of preferred) {
    const component = addressComponents.find((item) => Array.isArray(item?.types) && item.types.includes(type));
    const text = component?.longText || component?.long_name || component?.shortText || component?.short_name;
    if (text) return String(text);
  }
  return "";
}

function googleCountry(addressComponents: any[]): { country?: string; countryCode?: string } {
  const component = addressComponents.find((item) => Array.isArray(item?.types) && item.types.includes("country"));
  if (!component) return {};
  const country = String(component?.longText || component?.long_name || "").trim() || undefined;
  const countryCode = String(component?.shortText || component?.short_name || "").trim().toUpperCase() || undefined;
  return { country, countryCode };
}

function googleLocation(location: any): { latitude: number; longitude: number } | null {
  const latitude = typeof location?.lat === "function" ? location.lat() : Number(location?.lat);
  const longitude = typeof location?.lng === "function" ? location.lng() : Number(location?.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

function googlePlaceToPicked(place: any): PickedPlace | null {
  const coords = googleLocation(place?.location);
  if (!coords) return null;
  const displayName = typeof place?.displayName === "string" ? place.displayName : String(place?.displayName?.text || "");
  const address = String(place?.formattedAddress || displayName || "").trim();
  const name = displayName.trim() || address.split(",")[0]?.trim() || "";
  if (!name) return null;
  const addressComponents = Array.isArray(place?.addressComponents) ? place.addressComponents : [];
  const countryInfo = googleCountry(addressComponents);
  return {
    name,
    city: googleCity(addressComponents),
    ...countryInfo,
    address,
    latitude: coords.latitude,
    longitude: coords.longitude,
    types: googleRawTypes(place),
    source: "google",
    placeId: String(place?.id || "") || undefined,
    rating: Number.isFinite(Number(place?.rating)) ? Number(place.rating) : undefined,
    ratingCount: Number.isFinite(Number(place?.userRatingCount)) ? Number(place.userRatingCount) : undefined
  };
}


function googleLegacyPlaceToPicked(place: any): PickedPlace | null {
  const coords = googleLocation(place?.geometry?.location);
  if (!coords) return null;
  const name = String(place?.name || "").trim();
  if (!name) return null;
  const address = String(place?.formatted_address || place?.vicinity || name).trim();
  const addressComponents = Array.isArray(place?.address_components) ? place.address_components : [];
  const city = googleCity(addressComponents) || String(place?.vicinity || "").split(",")[0]?.trim() || "";
  const countryInfo = googleCountry(addressComponents);
  return {
    name,
    city,
    ...countryInfo,
    address,
    latitude: coords.latitude,
    longitude: coords.longitude,
    types: googleRawTypes(place),
    source: "google",
    placeId: String(place?.place_id || "") || undefined,
    rating: Number.isFinite(Number(place?.rating)) ? Number(place.rating) : undefined,
    ratingCount: Number.isFinite(Number(place?.user_ratings_total)) ? Number(place.user_ratings_total) : undefined
  };
}

async function fetchGooglePlaceById(placeId: string): Promise<{ picked: PickedPlace; raw: any } | null> {
  if (!placeId) return null;
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;
  if (!Place) return null;
  const raw = new Place({ id: placeId });
  await raw.fetchFields({
    fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType", "rating", "userRatingCount"]
  });
  const picked = googlePlaceToPicked(raw);
  return picked ? { picked, raw } : null;
}

const CITY_PLACE_TYPES = new Set([
  "locality",
  "postal_town",
  "administrative_area_level_3",
  "administrative_area_level_4"
]);

function googleRawTypes(place: any): string[] {
  const types = Array.isArray(place?.types) ? place.types.map((value: unknown) => String(value)) : [];
  const primary = String(place?.primaryType || "");
  return primary && !types.includes(primary) ? [primary, ...types] : types;
}

function isGoogleCityLikeRaw(place: any): boolean {
  return googleRawTypes(place).some((type) => CITY_PLACE_TYPES.has(type));
}

function isGoogleCityAreaTypes(types: string[]): boolean {
  return types.some((type) => CITY_PLACE_TYPES.has(String(type)));
}

function matchesKind(place: any, kind: PlacePickerKind): boolean {
  if (kind === "place") return true;
  if (kind === "city") return isGoogleCityLikeRaw(place);
  const types = googleRawTypes(place);
  const primary = String(place?.primaryType || "");
  if (kind === "experience") {
    const allowed = ["tourist_attraction","museum","amusement_park","art_gallery","historical_landmark","national_park","aquarium","zoo","stadium","performing_arts_theater","cultural_center","visitor_center","park","event_venue"];
    return allowed.some(type=>types.includes(type)||primary===type) || types.some(type=>/attraction|museum|gallery|historic|activity|event|tour/.test(type));
  }
  if (kind === "restaurant") {
    const foodTypes = ["restaurant", "cafe", "bar", "bakery", "meal_takeaway"];
    return foodTypes.some((type) => types.includes(type) || primary === type || primary.endsWith(`_${type}`) || primary.endsWith("_restaurant"));
  }
  const lodgingTypes = ["lodging", "hotel", "resort_hotel", "hostel", "motel", "inn", "guest_house", "bed_and_breakfast", "extended_stay_hotel"];
  return lodgingTypes.some((type) => types.includes(type) || primary === type);
}

async function googleTextSearch(
  term: string,
  kind: PlacePickerKind,
  language: string,
  bias?: PickedPlace | null,
  minRating = 0
): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;
  if (!Place?.searchByText) throw new Error("google-maps/places-unavailable");

  const request: Record<string, unknown> = {
    textQuery: term,
    fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType", "rating", "userRatingCount"],
    maxResultCount: 20,
    language
  };
  if (kind === "experience") {
    // A museum, guided tour or historical site is not always a tourist_attraction.
    // Search broadly and enforce the real category against the Places result types.
  } else if (kind === "restaurant") {
    request.includedType = "restaurant";
    request.useStrictTypeFiltering = false;
  } else if (kind === "hotel") {
    request.includedType = "hotel";
    request.useStrictTypeFiltering = false;
  }
  if (minRating > 0 && (kind === "restaurant" || kind === "hotel")) request.minRating = minRating;
  if (bias && Number.isFinite(bias.latitude) && Number.isFinite(bias.longitude)) {
    request.locationBias = { lat: bias.latitude, lng: bias.longitude };
  }

  const response = await Place.searchByText(request);
  const raw = Array.isArray(response?.places) ? response.places : [];
  return dedupe(raw.filter((place: any) => matchesKind(place, kind)).map(googlePlaceToPicked).filter((place: PickedPlace | null): place is PickedPlace => Boolean(place)));
}



async function googleCitySearch(term: string, language: string): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;

  if (Place?.searchByText) {
    try {
      const response = await Place.searchByText({
        textQuery: term,
        fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType"],
        includedType: "locality",
        useStrictTypeFiltering: true,
        maxResultCount: 20,
        language
      });
      const strictCities = dedupe(
        (Array.isArray(response?.places) ? response.places : [])
          .filter((place: any) => isGoogleCityLikeRaw(place))
          .map(googlePlaceToPicked)
          .filter((item: PickedPlace | null): item is PickedPlace => Boolean(item))
      );
      if (strictCities.length > 0) {
        return strictCities.sort((a, b) => {
          const aExact = isExactCityQuery(term, a) ? 2 : isCityQueryMatch(term, a) ? 1 : 0;
          const bExact = isExactCityQuery(term, b) ? 2 : isCityQueryMatch(term, b) ? 1 : 0;
          return bExact - aExact;
        });
      }
    } catch {
      // Some municipalities are not classified as locality; fall back to the city collection.
    }
  }

  const AutocompleteSuggestion = places?.AutocompleteSuggestion;
  const AutocompleteSessionToken = places?.AutocompleteSessionToken;
  if (!AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
    const fallbackArea = await googleGeocodeArea(term);
    return fallbackArea && isGoogleCityAreaTypes(fallbackArea.types) && isExactCityQuery(term, fallbackArea.place)
      ? [fallbackArea.place]
      : [];
  }

  const sessionToken = AutocompleteSessionToken ? new AutocompleteSessionToken() : undefined;
  const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input: term,
    includedPrimaryTypes: ["(cities)"],
    language,
    ...(sessionToken ? { sessionToken } : {})
  });

  const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
  const predictions = suggestions
    .map((suggestion: any) => suggestion?.placePrediction)
    .filter(Boolean)
    .slice(0, 8);

  const resolved = await Promise.all(predictions.map(async (prediction: any) => {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType"]
      });
      if (!isGoogleCityLikeRaw(place)) return null;
      const picked = googlePlaceToPicked(place);
      if (!picked) return null;
      return {
        ...picked,
        name: picked.name || picked.city,
        city: picked.city || picked.name
      };
    } catch {
      return null;
    }
  }));

  return dedupe(resolved.filter((item): item is PickedPlace => Boolean(item)))
    .sort((a, b) => {
      const aExact = isExactCityQuery(term, a) ? 2 : isCityQueryMatch(term, a) ? 1 : 0;
      const bExact = isExactCityQuery(term, b) ? 2 : isCityQueryMatch(term, b) ? 1 : 0;
      return bExact - aExact;
    });
}

function isExactCityQuery(term: string, place: PickedPlace | undefined): boolean {
  if (!place) return false;
  const query = normalizeText(term);
  if (!query) return false;
  const candidates = [
    place.name,
    place.city,
    place.address.split(",")[0] || ""
  ].map(normalizeText).filter(Boolean);
  return candidates.some((candidate) => candidate === query);
}

function isCityQueryMatch(term: string, place: PickedPlace | undefined): boolean {
  if (!place) return false;
  const query = normalizeText(term);
  if (!query) return false;
  const candidates = [
    place.name,
    place.city,
    place.address.split(",")[0] || ""
  ].map(normalizeText).filter(Boolean);
  return candidates.some((candidate) => candidate === query || candidate.startsWith(query) || query.startsWith(candidate));
}

function rankPlacesForArea(items: PickedPlace[], term: string, area?: PickedPlace | null): PickedPlace[] {
  const query = normalizeText(term);
  const areaNames = area ? [area.name, area.city, area.address.split(",")[0] || ""].map(normalizeText).filter(Boolean) : [];
  const score = (place: PickedPlace) => {
    const city = normalizeText(place.city);
    const address = normalizeText(place.address);
    const firstAddress = normalizeText(place.address.split(",")[0] || "");
    let value = 0;
    if (query && (city === query || firstAddress === query)) value += 120;
    else if (query && (city.startsWith(query) || firstAddress.startsWith(query))) value += 100;
    else if (query && address.includes(query)) value += 60;
    if (areaNames.some((name) => name && (city === name || firstAddress === name))) value += 80;
    else if (areaNames.some((name) => name && address.includes(name))) value += 40;
    value += Math.round((place.rating || 0) * 4);
    value += Math.min(10, Math.log10((place.ratingCount || 0) + 1) * 3);
    if (area) value -= Math.min(30, distanceKm(area, place) / 2);
    return value;
  };
  return [...items].sort((a, b) => score(b) - score(a) || (b.rating || 0) - (a.rating || 0) || (b.ratingCount || 0) - (a.ratingCount || 0) || a.name.localeCompare(b.name));
}

function distanceKm(a: PickedPlace, b: PickedPlace): number {
  const toRad = (value: number) => value * Math.PI / 180;
  const earthRadius = 6371;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const haversine = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

export async function searchGooglePointsOfInterest(
  city: PickedPlace,
  language: string
): Promise<PickedPlace[]> {
  if (!GOOGLE_MAPS_API_KEY) return [];
  const cityLabel = city.address || city.name;
  const queries = language === "it"
    ? [`attrazioni turistiche a ${cityLabel}`, `musei monumenti a ${cityLabel}`]
    : language === "es"
      ? [`atracciones turísticas en ${cityLabel}`, `museos monumentos en ${cityLabel}`]
      : [`tourist attractions in ${cityLabel}`, `museums landmarks in ${cityLabel}`];

  const collected: PickedPlace[] = [];
  for (const query of queries) {
    try {
      const found = await googleTextSearch(query, "place", language, city);
      collected.push(...found);
    } catch {
      // Try the next query.
    }
  }

  return dedupe(collected)
    .filter((place) => normalizeText(place.name) !== normalizeText(city.name))
    .filter((place) => 40 >= distanceKm(city, place))
    .slice(0, 20);
}

async function googleNearbySearch(
  area: PickedPlace,
  kind: Exclude<PlacePickerKind, "place" | "city">,
  radius: number
): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  await google.maps.importLibrary("places");
  const PlacesService = google.maps?.places?.PlacesService;
  if (!PlacesService) throw new Error("google-maps/nearby-unavailable");

  const service = new PlacesService(document.createElement("div"));
  const type = kind === "restaurant" ? "restaurant" : kind === "experience" ? "tourist_attraction" : "lodging";

  return await new Promise<PickedPlace[]>((resolve, reject) => {
    service.nearbySearch(
      {
        location: { lat: area.latitude, lng: area.longitude },
        radius,
        type
      },
      (raw: any[], status: string) => {
        const ok = google.maps.places.PlacesServiceStatus.OK;
        const zero = google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
        if (status === zero) {
          resolve([]);
          return;
        }
        if (status !== ok) {
          reject(new Error(`google-maps/nearby-${status}`));
          return;
        }
        const items = Array.isArray(raw) ? raw : [];
        resolve(
          dedupe(
            items
              .filter((place: any) => matchesKind(place, kind))
              .map(googleLegacyPlaceToPicked)
              .filter((place: PickedPlace | null): place is PickedPlace => Boolean(place))
          )
        );
      }
    );
  });
}

async function googleLegacyTextSearch(
  term: string,
  kind: Exclude<PlacePickerKind, "place" | "city">,
  bias?: PickedPlace | null
): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  await google.maps.importLibrary("places");
  const PlacesService = google.maps?.places?.PlacesService;
  if (!PlacesService) return [];

  const service = new PlacesService(document.createElement("div"));
  const request: Record<string, unknown> = { query: term };
  if (bias) {
    request.location = { lat: bias.latitude, lng: bias.longitude };
    request.radius = 25000;
  }

  return await new Promise<PickedPlace[]>((resolve, reject) => {
    service.textSearch(request, (raw: any[], status: string) => {
      const ok = google.maps.places.PlacesServiceStatus.OK;
      const zero = google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
      if (status === zero) {
        resolve([]);
        return;
      }
      if (status !== ok) {
        reject(new Error(`google-maps/text-${status}`));
        return;
      }
      const items = Array.isArray(raw) ? raw : [];
      resolve(
        dedupe(
          items
            .filter((place: any) => matchesKind(place, kind))
            .map(googleLegacyPlaceToPicked)
            .filter((place: PickedPlace | null): place is PickedPlace => Boolean(place))
        )
      );
    });
  });
}

async function googleGeocodeArea(term: string): Promise<GeocodedArea | null> {
  const google = await loadGoogleMaps();
  const geocoder = new google.maps.Geocoder();
  const response = await geocoder.geocode({ address: term });
  const results = Array.isArray(response?.results) ? response.results : [];
  const area = results.find((result: any) => {
    const types = Array.isArray(result?.types) ? result.types : [];
    return types.some((type: string) => ["locality", "postal_town", "administrative_area_level_4", "administrative_area_level_3", "administrative_area_level_2", "administrative_area_level_1", "sublocality", "neighborhood", "country"].includes(type));
  });
  if (!area) return null;
  const coords = googleLocation(area?.geometry?.location);
  if (!coords) return null;
  const place: PickedPlace = {
    name: String(area?.address_components?.[0]?.long_name || term).trim(),
    city: googleCity(Array.isArray(area?.address_components) ? area.address_components.map((component: any) => ({
      longText: component.long_name,
      shortText: component.short_name,
      types: component.types
    })) : []),
    address: String(area?.formatted_address || term).trim(),
    latitude: coords.latitude,
    longitude: coords.longitude,
    types: Array.isArray(area?.types) ? area.types.map(String) : [],
    source: "google",
    placeId: String(area?.place_id || "") || undefined
  };
  return { place, types: Array.isArray(area?.types) ? area.types.map(String) : [] };
}

function isAreaQuery(term: string, area: GeocodedArea | null): boolean {
  if (!area) return false;
  const query = normalizeText(term);
  if (!query) return false;
  const areaName = normalizeText(area.place.name);
  const address = normalizeText(area.place.address);
  const firstAddressPart = normalizeText(area.place.address.split(",")[0] || "");
  const queryTokens = query.split(" ").filter(Boolean);
  const addressContainsAllTokens = queryTokens.length > 0 && queryTokens.every((token) => address.includes(token));
  return query === areaName || query === firstAddressPart || addressContainsAllTokens;
}

async function searchActivitiesAroundArea(
  area: PickedPlace,
  kind: Exclude<PlacePickerKind, "place" | "city">,
  language: string,
  searchTerm: string
): Promise<PickedPlace[]> {
  let found: PickedPlace[] = [];
  const radii = [5000, 12000, 25000];
  for (const radius of radii) {
    try {
      found = dedupe([...found, ...(await googleNearbySearch(area, kind, radius))]);
    } catch {
      // Continue with wider searches and text search.
    }
    if (found.length >= 12) break;
  }

  const categoryQueries = kind === "restaurant"
    ? [`restaurants in ${area.address || area.name}`, `restaurants ${area.name}`]
    : kind === "experience"
      ? [`attractions museums experiences in ${area.address || area.name}`, `things to do in ${area.address || area.name}`]
      : [`hotels in ${area.address || area.name}`, `hotels ${area.name}`, `lodging in ${area.address || area.name}`];
  for (const query of categoryQueries) {
    try {
      const textFound = await googleTextSearch(query, kind, language, area);
      found = dedupe([...found, ...textFound]);
    } catch {
      // Try the next Google strategy.
    }
  }
  if (found.length < 8) {
    for (const query of categoryQueries) {
      try {
        const legacyFound = await googleLegacyTextSearch(query, kind, area);
        found = dedupe([...found, ...legacyFound]);
      } catch {
        // Return the results already collected.
      }
    }
  }
  return rankPlacesForArea(found, searchTerm, area).slice(0, 20);
}

export interface TripPhotoCandidate {
  placeId: string;
  photoIndex: number;
  name: string;
  address: string;
  url: string;
}

export async function fetchTripPhotoCandidates(destination: string, language: string, maxCount = 8): Promise<TripPhotoCandidate[]> {
  const term = destination.trim();
  if (!term || !GOOGLE_MAPS_API_KEY) return [];
  const candidates: TripPhotoCandidate[] = [];
  const seen = new Set<string>();

  const addPhotos = async (place: PickedPlace, count: number) => {
    if (!place.placeId || candidates.length >= maxCount) return;
    try {
      const photos = await fetchGooglePlacePhotos(place.placeId, count);
      photos.forEach((photo, photoIndex) => {
        const key = `${place.placeId}:${photoIndex}`;
        if (seen.has(key) || candidates.length >= maxCount) return;
        seen.add(key);
        candidates.push({
          placeId: place.placeId as string,
          photoIndex,
          name: place.name,
          address: place.address,
          url: photo.url
        });
      });
    } catch {
      // A destination can legitimately have no Google photos.
    }
  };

  let area: PickedPlace | null = null;
  try {
    area = (await googleGeocodeArea(term))?.place || null;
    if (area) await addPhotos(area, 2);
  } catch {
    area = null;
  }

  const queries = [`tourist attractions in ${term}`, `landmarks in ${term}`];
  for (const query of queries) {
    if (candidates.length >= maxCount) break;
    try {
      const places = await googleTextSearch(query, "place", language, area);
      for (const place of places.slice(0, 6)) {
        await addPhotos(place, 1);
        if (candidates.length >= maxCount) break;
      }
    } catch {
      // Try the next query.
    }
  }
  return candidates.slice(0, maxCount);
}

export async function fetchGooglePlacePhotos(placeId: string, maxCount = 6): Promise<GooglePlacePhotoChoice[]> {
  if (!placeId || !GOOGLE_MAPS_API_KEY) return [];
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;
  if (!Place) return [];
  const place = new Place({ id: placeId });
  await place.fetchFields({ fields: ["photos"] });
  const photos = Array.isArray(place.photos) ? place.photos.slice(0, maxCount) : [];
  return photos.flatMap((photo: any) => {
    const url = typeof photo?.getURI === "function" ? String(photo.getURI({ maxWidth: 1000, maxHeight: 700 }) || "") : "";
    if (!url) return [];
    const attribution = Array.isArray(photo?.authorAttributions) ? photo.authorAttributions[0] : undefined;
    return [{
      url,
      attributionName: String(attribution?.displayName || "").trim(),
      attributionUrl: String(attribution?.uri || "").trim() || undefined
    } satisfies GooglePlacePhotoChoice];
  });
}

function placeForKind(place: PickedPlace, kind: PlacePickerKind): PickedPlace {
  if (kind !== "city") return place;
  const cityPlace = { ...place };
  delete cityPlace.rating;
  delete cityPlace.ratingCount;
  return cityPlace;
}

function resultsFromPlaces(items: PickedPlace[], kind: PlacePickerKind): SearchResult[] {
  return items.map((rawPlace, index) => {
    const place = placeForKind(rawPlace, kind);
    return {
      id: place.placeId || `${place.source || "place"}-${index}-${place.latitude}-${place.longitude}`,
      name: place.name,
      city: place.city,
      description: place.description || (kind === "city" ? [place.city && normalizeText(place.city)!==normalizeText(place.name)?place.city:"",place.country||String(place.address||"").split(",").at(-1)?.trim()].filter(Boolean).join(" · ") || place.address : place.address),
      picked: place
    };
  });
}

function GeographicPlacePicker({
  destination,
  initialQuery,
  kind = "place",
  onPick,
  onClose,
  onManualEntry,
  manualEntryLabel,
  mapFirst = false,
  guidedSearch = false,
  autoSearch = false,
  onCustomExperience
}: {
  destination?: string;
  initialQuery?: string;
  kind?: PlacePickerKind;
  onPick: (place: PickedPlace) => void;
  onClose: () => void;
  onManualEntry?: () => void;
  manualEntryLabel?: string;
  mapFirst?: boolean;
  guidedSearch?: boolean;
  autoSearch?: boolean;
  onCustomExperience?: (name:string)=>void;
}) {
  const { t, language } = useI18n();
  const osmAttribution = ["OpenStreetMap", "contributors"].join(" ");
  const [query, setQuery] = useState(() => String(initialQuery || ""));
  const [rawResults, setResults] = useState<SearchResult[]>([]);
  const [minRating, setMinRating] = useState(0);
  const results = useMemo(() => rawResults.filter((result) => minRating === 0 || Math.max(minRating, result.picked.rating || 0) === (result.picked.rating || 0)), [rawResults, minRating]);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [selected, setSelected] = useState<PickedPlace | null>(null);
  const [mapCenter, setMapCenter] = useState<PickedPlace | null>(null);
  const [mapZoom, setMapZoom] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [providerUnavailable,setProviderUnavailable] = useState(false);
  const [searched, setSearched] = useState(false);
  const searchEpochRef = useRef(0);
  const [googleLoadError, setGoogleLoadError] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const googleMapNodeRef = useRef<HTMLDivElement | null>(null);
  const googleMapRef = useRef<any>(null);
  const googleMarkerRefs = useRef<any[]>([]);
  const googleMapClickListenerRef = useRef<any>(null);

  useEffect(() => {
    setQuery(String(initialQuery || ""));
  }, [initialQuery]);

  useEffect(() => {
    let cancelled = false;
    const initialEpoch = searchEpochRef.current;
    const canCenter = () => !cancelled && searchEpochRef.current === initialEpoch;
    const term = destination?.trim();
    if (!term) return;

    const centerDestination = async () => {
      if (USE_GOOGLE_PICKER) {
        try {
          const area = await googleGeocodeArea(term);
          if (canCenter() && area) {
            setMapCenter(area.place);
            setMapZoom(7);
            setGoogleLoadError(false);
            return;
          }
        } catch (nextError) {
          if (canCenter() && isLoaderError(nextError)) setGoogleLoadError(true);
        }
      }
      try {
        const fallback = await resilientFallbackSearch(term, undefined, language);
        if (canCenter() && fallback[0]) setMapCenter(fallback[0]);
      } catch {
        // The journey's destination is optional search bias, not a reason to
        // display an unrelated map or fail when a provider is temporarily down.
      }
    };

    void centerDestination();
    return () => { cancelled = true; };
  }, [destination, language]);

  const mapPlace = selected || mapCenter;
  useEffect(() => {
    if (!USE_GOOGLE_PICKER || !googleMapNodeRef.current || !mapPlace) return;
    let cancelled = false;
    void loadGoogleMaps().then(async (google) => {
      if (cancelled || !googleMapNodeRef.current) return;
      const center = { lat: mapPlace.latitude, lng: mapPlace.longitude };
      if (!googleMapRef.current) {
        const maps = await google.maps.importLibrary("maps");
        googleMapRef.current = new maps.Map(googleMapNodeRef.current, {
          center,
          zoom: mapZoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: true
        });
      }

      googleMapClickListenerRef.current?.remove?.();
      googleMapClickListenerRef.current = googleMapRef.current.addListener("click", (event: any) => {
        const placeId = String(event?.placeId || "").trim();
        if (!placeId) return;
        event?.stop?.();
        void fetchGooglePlaceById(placeId)
          .then((detail) => {
            if (!detail || !matchesKind(detail.raw, kind)) return;
            const result = resultsFromPlaces([detail.picked], kind)[0];
            if (result) selectAndUseResult(result);
          })
          .catch(() => undefined);
      });

      googleMarkerRefs.current.forEach((marker) => marker.setMap(null));
      googleMarkerRefs.current = [];

      const markerItems = results.length > 0 ? results : selected ? [{ id: "selected", picked: selected }] : [];
      const bounds = new google.maps.LatLngBounds();
      markerItems.forEach((item: any) => {
        const position = { lat: item.picked.latitude, lng: item.picked.longitude };
        const marker = new google.maps.Marker({ map: googleMapRef.current, position, title: item.picked.name });
        if (item.id !== "selected") {
          marker.addListener("click", () => {
            const result = results.find((candidate) => candidate.id === item.id);
            if (result) selectAndUseResult(result);
          });
        }
        googleMarkerRefs.current.push(marker);
        bounds.extend(position);
      });

      if (selected) {
        googleMapRef.current.setCenter(center);
        googleMapRef.current.setZoom(17);
      } else if (results.length > 1) {
        googleMapRef.current.fitBounds(bounds, 48);
      } else {
        googleMapRef.current.setCenter(center);
        googleMapRef.current.setZoom(mapZoom);
      }
      setGoogleReady(true);
      setGoogleLoadError(false);
    }).catch((nextError) => {
      if (isLoaderError(nextError)) setGoogleLoadError(true);
    });
    return () => {
      cancelled = true;
      googleMapClickListenerRef.current?.remove?.();
      googleMapClickListenerRef.current = null;
    };
  }, [kind, mapPlace, mapZoom, results, selected]);

  const automaticSearch=useRef("");
  useEffect(()=>{const term=String(initialQuery||"").trim();const key=kind+"|"+term;if(autoSearch&&term&&query.trim()===term&&automaticSearch.current!==key){automaticSearch.current=key;void search()}},[autoSearch,initialQuery,query,kind]);
  async function search() {
    const term = query.trim();
    if (!term || loading) return;
    if(kind==="city"&&typeof document!=="undefined"&&document.activeElement instanceof HTMLElement)document.activeElement.blur();
    setLoading(true);
    setError(false);
    setProviderUnavailable(false);
    setSelected(null);
    setSelectedResultId(null);
    const cacheKey = `${kind}|${normalizeText(term)}|${normalizeText(destination||"")}`;
    const cached = recentPlaceResults.get(cacheKey);
    // Do not erase valid results before the provider has answered. This previously
    // caused empty lists and an unrelated map center after a transient failure.
    const store = (next: SearchResult[]) => {
      setResults(next);
      if (next.length) {
        recentPlaceResults.delete(cacheKey);
        recentPlaceResults.set(cacheKey,next);
        while (recentPlaceResults.size > 40) {
          const oldest = recentPlaceResults.keys().next().value;
          if (oldest) recentPlaceResults.delete(oldest); else break;
        }
      }
    };
    setSearched(true);
    const searchEpoch = ++searchEpochRef.current;
    const immediate=kind==="hotel"?verifiedLecceHotel(term,destination||""):
      kind==="experience"?verifiedSantiagoRoute(term,language):[];
    if (cached?.length) {store(cached);const point=cached.find(item=>Number.isFinite(item.picked.latitude)&&Number.isFinite(item.picked.longitude));setMapCenter(point?.picked||null);}
    else if (immediate.length) {store(resultsFromPlaces(immediate,kind));setMapCenter(null);}
    else {setResults([]);setMapCenter(null);}

    try {
      if (kind === "experience") {
        // Routes such as the Camino de Santiago are not POIs. Search real
        // experience titles first, preserving a verified cultural route fallback.
        const known=verifiedSantiagoRoute(term,language);
        let reference:PickedPlace[]=[];
        try { reference=await searchWikipediaExperiences(term,language); } catch { /* show sourced fallback or try geographical search */ }
        const titles=new Set<string>();
        const experiences=[...reference,...known].filter(item=>{const key=normalizeText(item.name);if(titles.has(key))return false;titles.add(key);return true;});
        if (experiences.length) {store(resultsFromPlaces(experiences,kind));setError(false);return;}
      }
      if (USE_GOOGLE_PICKER) {
        try {
          const cityMatches = await googleCitySearch(term, language);

          if (kind === "city") {
            if (cityMatches.length > 0) {
              store(resultsFromPlaces(cityMatches, kind));
              setMapCenter(cityMatches[0]);
              setMapZoom(cityMatches.length > 1 ? 7 : 10);
              setGoogleLoadError(false);
              return;
            }

            const area = await googleGeocodeArea(term);
            if (area && isGoogleCityAreaTypes(area.types) && isExactCityQuery(term, area.place)) {
              store(resultsFromPlaces([area.place], kind));
              setMapCenter(area.place);
              setMapZoom(10);
              setGoogleLoadError(false);
              return;
            }
          } else if (kind === "place") {
            const foundPlaces = await googleTextSearch(term, "place", language, null);
            if (foundPlaces.length > 0) {
              store(resultsFromPlaces(foundPlaces, kind));
              setMapCenter(foundPlaces[0]);
              setMapZoom(14);
              setGoogleLoadError(false);
              return;
            }

            const area = await googleGeocodeArea(term);
            if (area) {
              store(resultsFromPlaces([area.place], kind));
              setMapCenter(area.place);
              setMapZoom(12);
              setGoogleLoadError(false);
              return;
            }
          } else {
            const exactCity = cityMatches.find((candidate) => isExactCityQuery(term, candidate));
            const partialCity = cityMatches.find((candidate) => isCityQueryMatch(term, candidate));
            let searchArea = exactCity || partialCity || null;
            if (!searchArea) {
              try {
                const geocodedArea = await googleGeocodeArea(term);
                if (geocodedArea && (isAreaQuery(term, geocodedArea) || isCityQueryMatch(term, geocodedArea.place))) searchArea = geocodedArea.place;
                if (!searchArea) {
                  const cityGeocodedArea = await googleGeocodeArea(`${term} City`);
                  if (cityGeocodedArea && (isAreaQuery(term, cityGeocodedArea) || isCityQueryMatch(term, cityGeocodedArea.place))) searchArea = cityGeocodedArea.place;
                }
              } catch {
                searchArea = null;
              }
            }

            if (searchArea) {
              setMapCenter(searchArea);
              setMapZoom(13);
              const found = await searchActivitiesAroundArea(searchArea, kind, language, term);
              store(resultsFromPlaces(found, kind));
              setError(found.length === 0);
              setGoogleLoadError(false);
              return;
            }

            let found = await googleTextSearch(term, kind, language, null);

            if (found.length === 0) {
              found = await googleLegacyTextSearch(term, kind, null);
            }

            if (found.length === 0 && destination?.trim()) {
              const scopedTerm = `${term}, ${destination.trim()}`;
              found = await googleTextSearch(scopedTerm, kind, language, null);
              if (found.length === 0) {
                found = await googleLegacyTextSearch(scopedTerm, kind, null);
              }
            }

            if (found.length > 0) {
              found = rankPlacesForArea(found, term, null);
              store(resultsFromPlaces(found.slice(0, 20), kind));
              setMapCenter(found[0]);
              setMapZoom(15);
              setGoogleLoadError(false);
              return;
            }
          }

          // Google can be available in a desktop browser but unavailable inside an
          // Android WebView because of API-key/referrer restrictions. Do not stop
          // here: continue with the provider-independent fallback search below.
          setGoogleLoadError(false);
        } catch (nextError) {
          if (isLoaderError(nextError)) {
            setGoogleLoadError(true);
          } else {
            setGoogleLoadError(false);
          }
        }
      }

      // On native Android the Google JavaScript Places API is intentionally disabled.
      // A city query such as "Lecce" therefore needs an actual hotel/restaurant
      // directory search, not a geocoder returning the city itself.
      let categoryProviderFailed = false;
      if (kind === "hotel" || kind === "restaurant" || kind === "experience") {
        try {
          const matches = await resilientCitySearch(term, language);
          const city = matches.find(item => isExactCityQuery(term,item));
          if (city) {
            try {
              const directory = await searchOsmCategoryInCity(city,kind,more=>{if(searchEpochRef.current===searchEpoch)store(resultsFromPlaces(more,kind));});
              if (directory.length) {
                const additions=kind === "hotel"?verifiedLecceHotel(term,city.name):[];
                const named=new Set<string>();
                const combined=[...directory,...additions].filter(p=>{const key=normalizeText(p.name);if(named.has(key))return false;named.add(key);return true;});
                store(resultsFromPlaces(combined,kind));
                setMapCenter(city);
                setMapZoom(12);
                return;
              }
            } catch { categoryProviderFailed = true; }
          }
        } catch { /* Keep the exact-name search available when city geocoding fails. */ }
      }
      let fallback:PickedPlace[]=[];
      try {
        if(kind === "city") fallback=await resilientCitySearch(term,language);
        else if(kind === "hotel"||kind === "restaurant")fallback=await searchNamedAccommodation(term,destination,language,kind);
        else fallback=await resilientFallbackSearch(term,destination,language,kind==="place");
      } catch {categoryProviderFailed=true;}
      const typed=fallback.filter(place=>kind === "city"||kind === "place"||matchesFallbackCategory(place,kind));
      const verified=kind === "hotel"?verifiedLecceHotel(term,destination||""):[];
      const known=new Set<string>();
      const safeFallback=[...typed,...verified].filter(place=>{const key=normalizeText(place.name)+"|"+normalizeText(place.city);if(known.has(key))return false;known.add(key);return true;});
      if (safeFallback.length) {
        store(resultsFromPlaces(safeFallback, kind));
        const mappable=safeFallback.find(p=>Number.isFinite(p.latitude)&&Number.isFinite(p.longitude));
        setMapCenter(mappable||null);
        setMapZoom(kind === "city" ? 10 : 14);
      } else if (cached?.length) {
        store(cached);
        setMapCenter(cached[0].picked);
        setMapZoom(kind === "city" ? 10 : 14);
      } else {
        store([]);
        setMapCenter(null); // no stale map centered on an unrelated location
      }
      if (categoryProviderFailed && !safeFallback.length && !cached?.length) setProviderUnavailable(true);
      setError(safeFallback.length === 0 && !cached?.length);
    } catch {
      const known=kind==="hotel"?verifiedLecceHotel(term,destination||""):
        kind==="experience"?verifiedSantiagoRoute(term,language):[];
      if (cached?.length) {
        store(cached);
        const mappable=cached.find(item=>Number.isFinite(item.picked.latitude)&&Number.isFinite(item.picked.longitude));
        setMapCenter(mappable?.picked||null);
      } else if (known.length){store(resultsFromPlaces(known,kind));setMapCenter(null);}
      else {store([]);setMapCenter(null);}
      setProviderUnavailable(!cached?.length&&!known.length);
      setError(!cached?.length&&!known.length);
    } finally {
      setLoading(false);
    }
  }

  function selectAndUseResult(result: SearchResult) {
    const picked = placeForKind(result.picked, kind);
    setSelectedResultId(result.id);
    setError(false);
    setSelected(picked);
    if(Number.isFinite(picked.latitude)&&Number.isFinite(picked.longitude))setMapCenter(picked);
    setMapZoom(kind === "city" ? 10 : 17);
    onPick(picked);
  }

  function renderMapPreview() {
    // Experiences are selected from a text list: never force a geographic map.
    if (kind === "experience") return null;
    if (USE_GOOGLE_PICKER && !mapFirst && (!googleLoadError || googleReady)) {
      return <div className="place-picker-google-map" ref={googleMapNodeRef} aria-label={t("places.mapPreview")} />;
    }
    // Reuse Mundely's existing Leaflet + Esri classic base layers. No OSM iframe,
    // sidebar, map embed branding, or coast/route decorations can appear here.
    if (mapPlace || mapFirst) return <PlaceResultsMap label={t("places.mapPreview")}
      center={mapPlace ? {latitude:mapPlace.latitude,longitude:mapPlace.longitude} : null}
      zoom={mapPlace ? mapZoom : 2} points={results.map((item,index)=>({id:item.id,name:item.name,
      latitude:item.picked.latitude,longitude:item.picked.longitude,number:index+1}))}
      showResultButtons={false}
      onSelect={id=>{const found=results.find(item=>item.id===id);if(found)selectAndUseResult(found);}} />;
    return <div className="place-picker-map-empty">{t("places.mapEmpty")}</div>;
  }

  return (
    <div className="place-picker-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className={"place-picker-modal"+(mapFirst?" wishlist-map-picker":"")} role="dialog" aria-modal="true" aria-label={kind === "hotel" ? (language === "it" ? "Ricerca hotel" : language === "es" ? "Búsqueda de hoteles" : "Hotel search") : kind === "experience" ? (language === "it" ? "Ricerca esperienze" : language === "es" ? "Búsqueda de experiencias" : "Experience search") : kind === "restaurant" ? (language === "it" ? "Ricerca ristoranti" : language === "es" ? "Búsqueda de restaurantes" : "Restaurant search") : kind === "city" ? (language === "it" ? "Ricerca città" : language === "es" ? "Búsqueda de ciudades" : "City search") : (language === "it" ? "Ricerca luoghi" : language === "es" ? "Búsqueda de lugares" : "Place search")}>
        <div className="place-picker-header">
          <div>
            <p className="eyebrow">{kind === "city" ? (language === "it" ? "RICERCA CITTÀ" : language === "es" ? "BÚSQUEDA DE CIUDADES" : "CITY SEARCH") : kind === "hotel" ? (language === "it" ? "RICERCA HOTEL" : language === "es" ? "BÚSQUEDA DE HOTELES" : "HOTEL SEARCH") : kind === "restaurant" ? (language === "it" ? "RICERCA RISTORANTI" : language === "es" ? "BÚSQUEDA DE RESTAURANTES" : "RESTAURANT SEARCH") : kind === "experience" ? (language === "it" ? "RICERCA ESPERIENZE" : language === "es" ? "BÚSQUEDA DE EXPERIENCIAS" : "EXPERIENCE SEARCH") : (USE_GOOGLE_PICKER ? t("places.pickerEyebrow") : language === "it" ? "RICERCA LUOGHI" : language === "es" ? "BÚSQUEDA DE LUGARES" : "PLACE SEARCH")}</p>
            <p>{kind === "city" ? (language === "it" ? "Cerca esclusivamente città e comuni." : language === "es" ? "Busca solo ciudades y municipios." : "Search cities and municipalities only.") : kind === "experience" ? (language === "it" ? "Cerca attrazioni, musei e attività da vivere." : language === "es" ? "Busca atracciones, museos y actividades." : "Find attractions, museums and activities.") : t("places.pickerBody")}</p>
          </div>
          {mapFirst ? <ModalCloseButton onClose={onClose}/> : <button className="standard-modal-close place-picker-close" type="button" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>}
        </div>

        <div className={"place-picker-search-row"+(kind==="restaurant"?" restaurant-search-row":"")}>
          <input
            className="input"
            value={query}
            onChange={(event) => {setQuery(event.target.value);setProviderUnavailable(false);setError(false);setSearched(false);}}
            onKeyDown={(event) => { if (event.key === "Enter") void search(); }}
            placeholder={kind === "hotel" ? t("places.searchHotelPlaceholder") : kind === "restaurant" ? t("places.searchRestaurantPlaceholder") : kind === "city" ? t("places.searchCityPlaceholder") : kind === "experience" ? (language === "it" ? "Musei, attrazioni, attività…" : language === "es" ? "Museos, atracciones, actividades…" : "Museums, attractions, activities…") : t("places.searchPlaceholder")}
            autoFocus={!autoSearch}
          />
          {!guidedSearch && (kind === "hotel" || kind === "restaurant") && (
            <div className="place-picker-rating-filter">
              <span>{t("places.minRating")}</span>
              <ValueSelect label={t("places.minRating")} value={String(minRating)} onChange={value => setMinRating(Number(value) || 0)}
                options={[{value:"0",label:t("places.anyRating")}, ...["3", "3.5", "4", "4.5"].map(value => ({value,label:`${value}+`}))]} />
            </div>
          )}
          <button className="button primary" type="button" onClick={() => void search()} disabled={loading || !query.trim()}>
            {loading ? t("places.searching") : t("places.search")}
          </button>
        </div>

        {onManualEntry && !mapFirst && <div className="place-picker-manual"><button className="button secondary" type="button" onClick={onManualEntry}>{t("itinerary.enterManually")}</button></div>}
        {error && <div className="place-picker-inline-feedback" role="status">{providerUnavailable ? (language === "it" ? "Servizio di ricerca temporaneamente non disponibile. Riprova tra poco." : language === "es" ? "Servicio de búsqueda temporalmente no disponible. Vuelve a intentarlo." : "Search service temporarily unavailable. Please try again.") : t("places.searchNoResults")}</div>}
        {googleLoadError && !googleReady && <div className="place-picker-provider-note">{USE_GOOGLE_PICKER ? t("places.googleFallback") : t("places.googleNotConfigured")}</div>}

        <div className={kind === "experience" ? "place-picker-body place-picker-body-list-only" : "place-picker-body"}>
          <div className="place-picker-results">
            {results.length === 0 && !loading && !error ? (
              <div className="place-picker-empty">{searched ? t("places.searchNoResults") : t("places.searchHint")}</div>
            ) : results.map((result) => (
              <button
                key={result.id}
                type="button"
                className={selectedResultId === result.id ? "place-picker-result active" : "place-picker-result"}
                onClick={() => selectAndUseResult(result)}
              >
                <div className="place-picker-result-title">
                  <strong>{result.name}</strong>
                  {kind !== "city" && typeof result.picked.rating === "number" && (
                    <span className="place-rating-badge">★ {result.picked.rating.toFixed(1)}{result.picked.ratingCount ? ` · ${result.picked.ratingCount}` : ""}</span>
                  )}
                </div>
                {result.city && <span>{result.city}</span>}
                <small>{result.description}</small>
              </button>
            ))}
          </div>
          {results.some(item=>item.picked.source==="overpass")&&<small className="place-picker-osm-attribution">{!mapFirst && <>{language === "it" ? "Dati luoghi" : language === "es" ? "Datos de lugares" : "Place data"} © </>}<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" aria-label={osmAttribution}>{mapFirst ? (language === "it" ? "Fonte dei luoghi" : language === "es" ? "Fuente de lugares" : "Place data source") : osmAttribution}</a></small>}

          {kind !== "experience" && <div className="place-picker-map-panel">
            {renderMapPreview()}
            {selected && (
              <div className="place-picker-selected">
                <div><strong>{selected.name}</strong><small>{selected.address}</small></div>
                <button className="button primary" type="button" onClick={() => onPick(selected)}>{t("places.usePlace")}</button>
              </div>
            )}
          </div>}
        </div>
        {mapFirst && onManualEntry && <div className="wishlist-map-manual"><button className="button secondary mundely-secondary-action" type="button" onClick={onManualEntry}>{manualEntryLabel || t("itinerary.enterManually")}</button></div>}
        {kind === "experience" && guidedSearch && onCustomExperience && query.trim() && <button type="button" className="button secondary place-picker-custom-experience" onClick={()=>onCustomExperience(query.trim())}>{language === "it" ? "Aggiungi manualmente l’esperienza" : language === "es" ? "Añadir la experiencia manualmente" : "Add experience manually"}</button>}
      </section>
    </div>
  );
}

// Recover photos for saved map points that predate Google place identifiers.
// Name agreement and a small distance are both required before using the photo.
export async function fetchNamedPlacePhoto(point: PickedPlace): Promise<GooglePlacePhotoChoice | null> {
  if (!Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return null;
  const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g," ").trim();
  const words = normalize(point.name).split(" ").filter(word=>word.length>=3 && !["hotel","restaurant","cafe","the"].includes(word));
  if (!words.length) return null;
  const candidates = await googleTextSearch([point.name,point.city].filter(Boolean).join(" "), "place", "en", point);
  const match = candidates.find(candidate => 0.6 > distanceKm(point,candidate) && words.every(word=>normalize(candidate.name).includes(word)));
  if (!match?.placeId) return null;
  return (await fetchGooglePlacePhotos(match.placeId,1))[0] || null;
}

export function PlacePicker(props: Parameters<typeof GeographicPlacePicker>[0]&{tripStartDate?:string;tripEndDate?:string}) {
  return props.kind === "experience" ? <ExperienceSearchDialog {...props} mapMode={props.mapFirst}/> : <GeographicPlacePicker {...props}/>;
}
